import cx from 'classnames';
import {ReactNode} from 'react';
import {isInAppMode} from '~/common/checkout/utils/app';
import {useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {StepConfig} from '~/QuickEcards/types/misc';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {CloseLineIcon} from '~library/svg/CloseLineIcon';
import {DiamondIcon} from '~library/svg/DiamondIcon';

interface DefaultNavHeaderProps {
  currentRoute: StepConfig | null;
  totalSteps: number;
  isPremium: boolean;
  handleCloseButtonClick: () => void;
  linkPreview?: ReactNode;
  linkBack?: ReactNode;
  previewButtons?: ReactNode;
  onboardingModalButton?: ReactNode;
  ctaPortal?: boolean;
}

const CloseButton = ({onClick}: {onClick: () => void}) => (
  <Button
    id="closeBtn"
    data-qa-id="exit-create"
    className="fabric-header__back"
    variant="transparent"
    size="small"
    onClick={onClick}
    ariaLabel="Close"
  >
    <CloseLineIcon ratio={0.75} />
  </Button>
);

const StepLabel = ({
  currentRoute,
  totalSteps,
  isPremium,
}: {
  currentRoute: StepConfig | null;
  totalSteps: number;
  isPremium: boolean;
}) => {
  const isLargishAndUp = useIsLargishAndUp();

  const showSteps = currentRoute?.meta?.countSteps;
  const stepLabel = showSteps ? `${currentRoute?.id} of ${totalSteps}:` : '';
  const displayName = currentRoute?.displayName ?? '';

  return (
    <>
      <Typography variant="list2" size="large" color="pebble" className="fabric-header__step-count">
        {isPremium && (
          <span className="fabric-header__is-premium">
            <DiamondIcon ratio={16 / 14.0} />
            {isLargishAndUp && (
              <Typography variant="list2" as="span" size="large" color="roseWebText">
                Premium
              </Typography>
            )}
          </span>
        )}
        {isPremium && showSteps && ' '}
        {stepLabel}
      </Typography>
      <Typography variant="list2" as="h1" size="large" className="fabric-header__step-name">
        &nbsp;
        {displayName}
      </Typography>
    </>
  );
};

export const DefaultNavHeader = ({
  currentRoute,
  isPremium,
  totalSteps,
  handleCloseButtonClick,
  linkPreview = null,
  linkBack = null,
  previewButtons = null,
  onboardingModalButton = null,
  ctaPortal = false,
}: DefaultNavHeaderProps) => {
  const inAppMode = isInAppMode();
  const currentRouteId = currentRoute?.id ?? 1;

  return (
    <nav className={`fabric-header__nav${currentRouteId > totalSteps ? ' frp' : ''}`}>
      <div className="fabric-header__left-wrap">
        {!inAppMode && <CloseButton onClick={handleCloseButtonClick} />}
        <div className="fabric-header__step-wrap">
          <StepLabel currentRoute={currentRoute} totalSteps={totalSteps} isPremium={isPremium} />
        </div>
        {linkPreview}
        {linkBack}

        {previewButtons}
        <div
          id="fabric-header__cta-portal"
          className={cx('fabric-header__cta-portal', {
            'fabric-header__cta-portal__hidden': !ctaPortal,
          })}
        />
      </div>
      {onboardingModalButton}
    </nav>
  );
};
